import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * HeroMap — Interactive Google Map that replaces the hero background image.
 * Shows dish-name tag markers for each recommendation.
 * Props:
 *   recommendations — array of { id, name, restaurant, location, price, rating }
 *   locationString — user's city/area string for centering
 *   onMarkerClick — optional callback when a marker is tapped
 *   highlightId — optional id to highlight a specific marker
 */
const GEOCODE_CACHE = {};

const MAP_STYLE = [
  { featureType: 'all', elementType: 'geometry', stylers: [{ saturation: -20 }] },
  { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#d4e9f7' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e0e0e0' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

async function geocodeLocation(query) {
  if (GEOCODE_CACHE[query]) return GEOCODE_CACHE[query];
  if (!window.google?.maps?.Geocoder) return null;

  return new Promise((resolve) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: query }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        const coords = { lat: loc.lat(), lng: loc.lng() };
        GEOCODE_CACHE[query] = coords;
        resolve(coords);
      } else {
        resolve(null);
      }
    });
  });
}

function createTagOverlay(map, position, label, price, isHighlighted, onClick) {
  class TagOverlay extends window.google.maps.OverlayView {
    constructor() {
      super();
      this.position = position;
      this.div = null;
    }
    onAdd() {
      this.div = document.createElement('div');
      this.div.style.cssText = 'position:absolute;cursor:pointer;transform:translate(-50%,-100%);z-index:1;transition:transform 0.15s ease';
      this.div.innerHTML = `
        <div style="
          display:flex;align-items:center;gap:4px;
          padding:5px 10px;border-radius:20px;
          background:${isHighlighted ? '#FF6B35' : '#fff'};
          color:${isHighlighted ? '#fff' : '#1a1a1a'};
          font-size:11px;font-weight:700;
          box-shadow:0 2px 8px rgba(0,0,0,0.15);
          white-space:nowrap;border:1.5px solid ${isHighlighted ? '#FF6B35' : '#e5e5e5'};
          font-family:system-ui,-apple-system,sans-serif;
        ">
          ${label.length > 18 ? label.substring(0, 18) + '...' : label}
          ${price ? `<span style="opacity:0.6;font-size:10px">${price}</span>` : ''}
        </div>
        <div style="width:8px;height:8px;background:${isHighlighted ? '#FF6B35' : '#fff'};border-right:1.5px solid ${isHighlighted ? '#FF6B35' : '#e5e5e5'};border-bottom:1.5px solid ${isHighlighted ? '#FF6B35' : '#e5e5e5'};transform:rotate(45deg);margin:-5px auto 0;"></div>
      `;
      this.div.addEventListener('click', () => onClick?.());
      this.div.addEventListener('mouseenter', () => { this.div.style.transform = 'translate(-50%,-100%) scale(1.1)'; this.div.style.zIndex = '10'; });
      this.div.addEventListener('mouseleave', () => { this.div.style.transform = 'translate(-50%,-100%)'; this.div.style.zIndex = '1'; });
      this.getPanes().overlayMouseTarget.appendChild(this.div);
    }
    draw() {
      const proj = this.getProjection();
      if (!proj) return;
      const point = proj.fromLatLngToDivPixel(new window.google.maps.LatLng(this.position.lat, this.position.lng));
      if (point) {
        this.div.style.left = point.x + 'px';
        this.div.style.top = point.y + 'px';
      }
    }
    onRemove() {
      this.div?.parentNode?.removeChild(this.div);
      this.div = null;
    }
    updateHighlight(hl) {
      if (!this.div) return;
      const inner = this.div.firstElementChild;
      if (inner) {
        inner.style.background = hl ? '#FF6B35' : '#fff';
        inner.style.color = hl ? '#fff' : '#1a1a1a';
        inner.style.borderColor = hl ? '#FF6B35' : '#e5e5e5';
      }
    }
  }
  const overlay = new TagOverlay();
  overlay.setMap(map);
  return overlay;
}

export default function HeroMap({ recommendations = [], locationString = '', onMarkerClick, highlightId }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const overlays = useRef([]);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !window.google?.maps?.Map) {
      setReady(false);
      return;
    }
    if (mapInstance.current) return;

    try {
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        zoom: 13,
        center: { lat: -33.8688, lng: 151.2093 },
        disableDefaultUI: true,
        zoomControl: true,
        styles: MAP_STYLE,
        gestureHandling: 'cooperative',
        zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_TOP },
      });

      // Detect auth failure — Google Maps fires 'tilesloaded' only on success
      const errorCheck = setTimeout(() => {
        if (mapRef.current?.querySelector('.gm-err-container')) {
          // Hide Google's error UI
          const errEl = mapRef.current.querySelector('.gm-err-container');
          if (errEl) errEl.style.display = 'none';
          setFailed(true);
          setReady(false);
          mapInstance.current = null;
        }
      }, 5000);

      mapInstance.current.addListener('tilesloaded', () => {
        clearTimeout(errorCheck);
        setReady(true);
      });

      if (locationString) {
        geocodeLocation(locationString).then(coords => {
          if (coords && mapInstance.current) mapInstance.current.setCenter(coords);
        });
      }
    } catch {
      setReady(false);
      setFailed(true);
    }
  }, [locationString]);

  // Place markers for recommendations
  const placeMarkers = useCallback(async () => {
    if (!mapInstance.current || !ready) return;

    // Clear old overlays
    overlays.current.forEach(o => o.setMap(null));
    overlays.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    let hasAny = false;

    for (let i = 0; i < recommendations.length; i++) {
      const rec = recommendations[i];
      const query = `${rec.restaurant || rec.name}, ${rec.location || locationString}`;
      const coords = await geocodeLocation(query);
      if (!coords) continue;

      // Slight offset for overlapping markers
      const offset = { lat: coords.lat + (Math.random() - 0.5) * 0.001, lng: coords.lng + (Math.random() - 0.5) * 0.001 };

      const overlay = createTagOverlay(
        mapInstance.current,
        offset,
        rec.name,
        rec.priceRange || rec.price || '',
        rec.id === highlightId,
        () => onMarkerClick?.(rec.id),
      );
      overlays.current.push(overlay);
      bounds.extend(new window.google.maps.LatLng(offset.lat, offset.lng));
      hasAny = true;
    }

    if (hasAny && mapInstance.current) {
      mapInstance.current.fitBounds(bounds, { top: 80, bottom: 20, left: 40, right: 40 });
      const listener = mapInstance.current.addListener('idle', () => {
        if (mapInstance.current.getZoom() > 15) mapInstance.current.setZoom(15);
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [recommendations, locationString, highlightId, onMarkerClick, ready]);

  useEffect(() => { placeMarkers(); }, [placeMarkers]);

  // Update highlight without re-geocoding
  useEffect(() => {
    overlays.current.forEach((o, i) => {
      if (recommendations[i]) {
        o.updateHighlight?.(recommendations[i].id === highlightId);
      }
    });
  }, [highlightId, recommendations]);

  if (failed) return null;

  return (
    <div ref={mapRef} className="absolute inset-0" data-testid="hero-map" style={{ minHeight: '100%' }}>
      <style>{`.gm-err-container, .gm-err-title, .gm-err-message, .gm-err-autocomplete { display: none !important; }`}</style>
    </div>
  );
}
